/**
 * Everybody on a ticket, gathered from the mail rather than from the contact.
 *
 * A ticket has one contact -- the person the thread is filed against -- and
 * for a long time that was also the only address any screen showed. Which is
 * wrong about most real threads: an RMA is opened by one person and Ccd to
 * their facilities manager, their account manager and a supplier, and an agent
 * about to reply needs to know that before they write "as discussed" to what
 * they think is one reader.
 *
 * Derived rather than stored, and it has to be: there is no table of ticket
 * participants and there should not be one. The truth is the To and Cc lines
 * of the messages themselves, and a stored copy would be a second answer that
 * goes stale the moment somebody replies-all with one more person on it.
 *
 * The reply composer's `replyAllCc` makes the same collection over a single
 * message and drops the same addresses; this is that question asked of the
 * whole thread.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "./api";
import { queryKeys } from "./queryKeys";
import type { EmailAddress, Message } from "../types";

export interface Participant {
  address: EmailAddress;
  /**
   * They have written on this thread, rather than only ever been copied on it.
   *
   * Worth separating because it is the difference between somebody taking part
   * and somebody being kept informed, and a reply reads differently to each.
   */
  wrote: boolean;
  /**
   * Every appearance has been on a Cc line.
   *
   * False the moment they are addressed directly once -- being the To line of
   * one message in a thread makes somebody a recipient of it, not an observer.
   */
  copied: boolean;
}

/**
 * The people on a thread, senders first, then everyone else in the order they
 * first appeared.
 *
 * `ours` is every address the desk sends from. They are dropped for the reason
 * the composer drops them: our own aliases are not participants in a
 * conversation we are hosting, and listing them makes every ticket look like
 * it has one more person on it than it does.
 *
 * Notes are skipped outright. An internal note has no From and no recipients,
 * so it would contribute nothing -- but saying so here means a note that grows
 * an author address later cannot quietly start putting colleagues into a list
 * of the customer's people.
 */
export function participantsOf(
  messages: readonly Message[],
  ours: readonly string[],
): Participant[] {
  const skip = new Set(ours.map((address) => address.trim().toLowerCase()));
  const found = new Map<string, Participant>();

  const add = (
    entry: EmailAddress | null,
    role: { wrote: boolean; copied: boolean },
  ): void => {
    if (!entry) return;
    const key = entry.email.trim().toLowerCase();
    if (!key || skip.has(key)) return;

    const seen = found.get(key);
    if (!seen) {
      found.set(key, { address: entry, wrote: role.wrote, copied: role.copied });
      return;
    }

    seen.wrote ||= role.wrote;
    seen.copied &&= role.copied;
    /* A later appearance with a name beats an earlier bare address: mail from
       a person carries their name, and being Ccd by somebody else often does
       not. */
    if (!seen.address.name && entry.name) seen.address = entry;
  };

  for (const message of messages) {
    if (message.kind === "note") continue;

    if (message.fromEmail) {
      add(
        { name: message.fromName ?? undefined, email: message.fromEmail },
        { wrote: true, copied: false },
      );
    }
    for (const entry of message.toRecipients ?? []) {
      add(entry, { wrote: false, copied: false });
    }
    for (const entry of message.ccRecipients ?? []) {
      add(entry, { wrote: false, copied: true });
    }
  }

  /*
   * Stable within each group: `Map` preserves insertion order, and messages
   * arrive oldest first, so "who started this" stays at the top rather than
   * moving about as the thread grows.
   */
  const people = [...found.values()];
  return [
    ...people.filter((person) => person.wrote),
    ...people.filter((person) => !person.wrote),
  ];
}

/** `Name <address>`, or the bare address when there is no name. */
export function describeParticipant(person: Participant): string {
  const { name, email } = person.address;
  return name ? `${name} <${email}>` : email;
}

/**
 * The thread's people, with the desk's own addresses already taken out.
 *
 * A hook rather than a bare call because "which addresses are ours" is a
 * request, and both screens that show this list would otherwise have to
 * remember to make it -- one that forgot would quietly list our own support
 * alias as somebody the customer had copied in.
 *
 * `channelAddress` is the alias this ticket arrived at, and it is passed as
 * well as the grant list rather than instead of it: an agent with no send-from
 * grants gets an empty list back, and the one address guaranteed to be on this
 * thread is the one it was sent to.
 */
export function useThreadParticipants(
  messages: readonly Message[] | undefined,
  channelAddress: string | undefined,
): Participant[] {
  const sendFrom = useQuery({
    queryKey: queryKeys.sendFrom,
    queryFn: api.sendFrom,
    staleTime: 5 * 60_000,
  });

  return useMemo(() => {
    if (!messages || messages.length === 0) return [];
    const ours = (sendFrom.data ?? []).map((entry) => entry.address);
    return participantsOf(
      messages,
      channelAddress ? [...ours, channelAddress] : ours,
    );
  }, [messages, channelAddress, sendFrom.data]);
}
